import { useCallback, useEffect, useRef, useState } from "react";

/* The Web Speech API isn't in TypeScript's DOM lib yet, and Chrome and
   Safari still ship it under the webkit prefix. These are the few members
   this hook touches. */
type SpeechResultList = ArrayLike<ArrayLike<{ transcript: string }>>;
type Recognition = {
    lang: string;
    interimResults: boolean;
    continuous: boolean;
    onresult: ((event: { results: SpeechResultList }) => void) | null;
    onerror: ((event: { error: string }) => void) | null;
    onend: (() => void) | null;
    start: () => void;
    stop: () => void;
    abort: () => void;
};
type RecognitionCtor = new () => Recognition;

const recognitionCtor = (): RecognitionCtor | undefined => {
    if (typeof window === "undefined") return undefined;
    const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
    return w.SpeechRecognition || w.webkitSpeechRecognition;
};

/* Errors that mean voice can't work in this browser at all, as opposed to
   one attempt failing. Brave, for one, exposes the API but fails every
   call with "network". */
const FATAL_ERRORS = new Set(["network", "service-not-allowed", "language-not-supported"]);

/**
 * Dictation for a text field. `onText` receives the running transcript of
 * the current utterance, interim words included, so the field fills in as
 * the traveller speaks. `supported` is false where the browser has no
 * speech recognition, and turns false if it proves unusable.
 */
export const useSpeechInput = (onText: (text: string) => void) => {
    const [supported, setSupported] = useState(() => !!recognitionCtor());
    const [listening, setListening] = useState(false);
    const [blocked, setBlocked] = useState(false);
    const recognitionRef = useRef<Recognition | null>(null);
    const onTextRef = useRef(onText);
    onTextRef.current = onText;

    useEffect(() => () => recognitionRef.current?.abort(), []);

    const start = useCallback(() => {
        const Ctor = recognitionCtor();
        if (!Ctor) return;
        const recognition = new Ctor();
        recognition.lang = navigator.language || "en-US";
        recognition.interimResults = true;
        recognition.continuous = false;
        recognition.onresult = (event) => {
            let text = "";
            for (let i = 0; i < event.results.length; i += 1) text += event.results[i][0].transcript;
            onTextRef.current(text);
        };
        recognition.onerror = (event) => {
            if (event.error === "not-allowed") setBlocked(true);
            if (FATAL_ERRORS.has(event.error)) setSupported(false);
        };
        recognition.onend = () => {
            recognitionRef.current = null;
            setListening(false);
        };
        recognitionRef.current = recognition;
        setBlocked(false);
        try {
            recognition.start();
            setListening(true);
        } catch {
            recognitionRef.current = null;
        }
    }, []);

    const toggle = useCallback(() => {
        if (recognitionRef.current) recognitionRef.current.stop();
        else start();
    }, [start]);

    /* Drops the utterance in progress without writing any more of it, for
       when the field has just been sent. `stop` would still deliver the
       final words, into the now-empty field. */
    const cancel = useCallback(() => recognitionRef.current?.abort(), []);

    return { supported, listening, blocked, toggle, cancel };
};
